import classNames from 'classnames'
import React from 'react'

import { IconComponent } from '../types'

export type ButtonVariants = 'primary' | 'secondary' | 'outlined'

export type ButtonSizes = 'small' | 'medium' | 'large'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariants
  size?: ButtonSizes
  label?: string
  block?: boolean
  prefixIcon?: IconComponent
  suffixIcon?: IconComponent
  className?: string
  loading?: boolean
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'secondary',
  label,
  block,
  size = 'medium',
  prefixIcon: PrefixIcon,
  suffixIcon: SuffixIcon,
  className,
  loading = false,
  ...buttonProps
}) => {
  return (
    <button
      {...buttonProps}
      className={classNames(
        className,
        'rounded-3xl cursor-pointer text-center outline-none inline-flex items-center justify-center gap-[.5em] leading-none disabled:opacity-50 disabled:pointer-events-none',
        {
          'hover:brightness-105': !buttonProps.disabled && !loading,
          'brightness-150 grayscale pointer-events-none': buttonProps.disabled || loading,
          'bg-primary text-white': variant === 'primary',
          'bg-neutralBlack-600/20 text-black ': variant === 'secondary',
          'border-[1.5px] border-neutralBlack-600 text-neutralBlack-600 hover:text-black hover:border-black':
            variant === 'outlined',
          'w-full': block,
          'min-h-14 px-8 text-xl': size === 'large',
          'min-h-12 px-6 text-lg': size === 'medium',
          'min-h-8 px-3 text-sm font-medium': size === 'small'
        }
      )}
      disabled={loading || buttonProps.disabled}
    >
      {loading && (
        <svg
          className="animate-spin h-5 w-5 text-primary mr-2"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 50 50"
        >
          <circle
            className="opacity-25"
            cx="25"
            cy="25"
            r="20"
            stroke="white"
            strokeWidth="5"
            fill="none"
          ></circle>
          <circle
            className="opacity-75"
            cx="25"
            cy="25"
            r="20"
            stroke="currentColor"
            strokeWidth="5"
            strokeLinecap="round"
            fill="none"
            strokeDasharray="31.415, 31.415"
            strokeDashoffset="0"
          >
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 25 25"
              to="360 25 25"
              dur="3s"
              repeatCount="indefinite"
            />
          </circle>
        </svg>
      )}

      {PrefixIcon && !loading && <PrefixIcon className="size-[1.35em] -translate-y-px" />}

      {(children || label) && (
        <span className="whitespace-nowrap overflow-hidden text-ellipsis">{children || label}</span>
      )}

      {SuffixIcon && !loading && <SuffixIcon className="size-[1.35em] -translate-y-px" />}
    </button>
  )
}
